import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  getComplianceOverview,
  type ComplianceOverview,
  type FrameworkReadiness,
  type Severity,
} from "@/lib/api";

/**
 * Readiness is computed, never a dropdown — this page is the blocker list
 * with a number on top, not a number with a story (COMPLIANCE.md, principle 4).
 *
 * The readiness figure is a METER, not a three-segment stacked bar. The
 * portal's status tokens fail an adjacent-pair check when ready/needs-work/
 * not-implemented sit side by side coloured only by hue — in light mode
 * danger and warning measure ΔE 11.4 to normal vision, below the 15 floor.
 * A meter shows one state's colour at a time and puts the other two counts in
 * text, so nothing depends on telling two hues apart.
 */

const SEVERITY_STYLE: Record<Severity, { label: string; icon: string; cls: string }> = {
  critical: { label: "Critical", icon: "fa-circle-exclamation", cls: "text-danger" },
  high: { label: "High", icon: "fa-triangle-exclamation", cls: "text-warning" },
  medium: { label: "Medium", icon: "fa-circle-info", cls: "text-text-muted" },
};

export function ComplianceOverviewPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["compliance-overview"],
    queryFn: getComplianceOverview,
  });
  const [severity, setSeverity] = useState<Severity | "all">("all");
  const [area, setArea] = useState<string>("all");

  const areas = useMemo(
    () => [...new Set((data?.blockers ?? []).map((b) => b.area ?? "other"))].sort(),
    [data],
  );
  const blockers = (data?.blockers ?? []).filter(
    (b) =>
      (severity === "all" || (b.severity ?? "high") === severity) &&
      (area === "all" || (b.area ?? "other") === area),
  );
  const counts = useMemo(() => {
    const c: Record<Severity, number> = { critical: 0, high: 0, medium: 0 };
    for (const b of data?.blockers ?? []) c[b.severity ?? "high"] += 1;
    return c;
  }, [data]);

  return (
    <div>
      <header className="mb-8">
        <h1 className="page-title">Compliance</h1>
        <p className="mt-2 text-text-secondary">
          ISO 27001 and SOC 2 in one control library — how ready we are, measured against
          what the standards ask, and exactly what to fix next.
        </p>
      </header>

      {isLoading || !data ? (
        <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-text-muted">
          <i className="fa-solid fa-circle-notch fa-spin text-lg" />
        </div>
      ) : (
        <>
          {data.readiness && data.readiness.length > 0 && (
            <div className="mb-6 grid gap-4 md:grid-cols-2">
              {data.readiness.map((f) => (
                <ReadinessCard key={f.key} f={f} />
              ))}
            </div>
          )}

          <Registers data={data} />

          <section className="mb-6">
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <h2 className="font-display text-lg font-semibold text-text-heading">
                What to fix next
              </h2>
              <span className="text-sm text-text-muted">
                {counts.critical > 0 && (
                  <span className="text-danger">{counts.critical} critical · </span>
                )}
                {counts.high} high · {counts.medium} medium
              </span>
              <div className="ml-auto flex gap-2">
                <select
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value as Severity | "all")}
                  className="rounded-md border border-border bg-bg-card px-2 py-1.5 text-sm text-text-heading"
                >
                  <option value="all">All severities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                </select>
                <select
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  className="rounded-md border border-border bg-bg-card px-2 py-1.5 text-sm capitalize text-text-heading"
                >
                  <option value="all">All areas</option>
                  {areas.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {blockers.length === 0 ? (
              <p className="rounded-token-lg border border-border bg-bg-card px-4 py-6 text-sm text-text-secondary">
                {(data.blockers ?? []).length === 0
                  ? "Nothing outstanding — every applicable control is owned, mapped, justified and approved."
                  : "Nothing matches this filter."}
              </p>
            ) : (
              <div className="overflow-hidden rounded-token-lg border border-border bg-bg-card">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-[0.65rem] uppercase tracking-wider text-text-muted">
                      <th className="px-4 py-2.5 font-semibold">Severity</th>
                      <th className="px-4 py-2.5 font-semibold">Where</th>
                      <th className="px-4 py-2.5 font-semibold">Rule</th>
                      <th className="px-4 py-2.5 font-semibold">What's missing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blockers.map((b, i) => {
                      const s = SEVERITY_STYLE[b.severity ?? "high"];
                      return (
                        <tr
                          key={`${b.objectId}-${b.rule}-${i}`}
                          className="border-b border-border-light"
                        >
                          {/* Icon + label, never colour alone. */}
                          <td className={`whitespace-nowrap px-4 py-2.5 ${s.cls}`}>
                            <i className={`fa-solid ${s.icon} mr-1.5 text-[0.7rem]`} />
                            {s.label}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2.5">
                            <Link to={linkFor(b)} className="font-medium text-accent hover:underline">
                              {b.refLabel}
                            </Link>
                            <span className="ml-2 text-text-secondary">{b.name}</span>
                          </td>
                          <td className="px-4 py-2.5">
                            <code className="rounded bg-bg-secondary px-1.5 py-0.5 text-xs text-text">
                              {b.rule}
                            </code>
                          </td>
                          <td className="px-4 py-2.5 text-text-secondary">{b.message}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {data.readiness?.some((f) => f.notImplemented > 0) && (
            <section>
              <h2 className="mb-3 font-display text-lg font-semibold text-text-heading">
                Requirements with no control
              </h2>
              <div className="grid gap-4 md:grid-cols-2">
                {data.readiness.map((f) => {
                  const missing = f.requirements.filter((r) => r.state === "not_implemented");
                  return (
                    <div key={f.key} className="rounded-token-lg border border-border bg-bg-card p-4">
                      <h3 className="mb-2 text-sm font-semibold text-text-heading">
                        {f.name}{" "}
                        <span className="font-normal text-text-muted">
                          · {missing.length} uncovered
                        </span>
                      </h3>
                      {missing.length === 0 ? (
                        <p className="text-sm text-text-secondary">Full coverage.</p>
                      ) : (
                        <ul className="max-h-64 space-y-1 overflow-y-auto text-sm">
                          {missing.map((r) => (
                            <li key={r.requirementId} className="text-text-secondary">
                              <span className="font-medium text-text-heading">{r.ref}</span>{" "}
                              {r.title}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <p className="mt-6 text-xs text-text-muted">
            {data.provisional.requirements + data.provisional.controls} rows are still
            provisional — written from public references and awaiting a check against the
            licensed standards.
          </p>
        </>
      )}
    </div>
  );
}

function linkFor(b: ComplianceOverview["blockers"][number]): string {
  switch (b.kind) {
    case "policy":
      return `/compliance/policies?open=${b.objectId}`;
    case "occurrence":
      return `/compliance/operating?open=${b.objectId}`;
    case "risk":
      return `/compliance/risks?open=${b.objectId}`;
    case "soa":
      return "/compliance/soa";
    case "person":
      return `/compliance/people?open=${b.objectId}`;
    case "vendor":
      return `/compliance/vendors?open=${b.objectId}`;
    case "finding":
      return `/compliance/audit?open=${b.objectId}`;
    case "audit":
      return "/compliance/audit";
    default:
      return `/compliance/controls?open=${b.objectId}`;
  }
}

/**
 * A meter, per the form heuristic: one ratio against a limit. The fill carries
 * severity and the track is the same hue at low alpha, so the state reads
 * across the whole bar; the other two counts sit beside it as text rather than
 * as segments that would have to be told apart by hue.
 */
function ReadinessCard({ f }: { f: FrameworkReadiness }) {
  const tone =
    f.percent >= 90 ? "success" : f.percent >= 50 ? "warning" : "danger";
  const fill = { success: "bg-success", warning: "bg-warning", danger: "bg-danger" }[tone];
  const track = { success: "bg-success/15", warning: "bg-warning/15", danger: "bg-danger/15" }[tone];

  return (
    <div className="rounded-token-lg border border-border bg-bg-card p-5">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h2 className="font-display text-sm font-semibold text-text-heading">{f.name}</h2>
          <p className="text-xs text-text-muted">{f.version}</p>
        </div>
        {/* Hero figure: sans, not the serif face, per the mark specs. */}
        <div className="font-display text-5xl font-semibold leading-none text-text-heading">
          {f.percent}
          <span className="ml-0.5 text-2xl font-normal text-text-muted">%</span>
        </div>
      </div>

      <div
        className={`mt-4 h-2.5 w-full overflow-hidden rounded-full ${track}`}
        role="meter"
        aria-valuenow={f.percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${f.name} readiness`}
      >
        <div className={`h-full rounded-full ${fill}`} style={{ width: `${f.percent}%` }} />
      </div>

      <dl className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs">
        <Count label="ready" value={f.ready} icon="fa-circle-check" cls="text-success" />
        <Count label="need work" value={f.needsWork} icon="fa-circle-half-stroke" cls="text-warning" />
        <Count
          label="not implemented"
          value={f.notImplemented}
          icon="fa-circle-minus"
          cls="text-danger"
        />
        <span className="text-text-muted">of {f.total} requirements</span>
      </dl>
    </div>
  );
}

function Count({
  label,
  value,
  icon,
  cls,
}: {
  label: string;
  value: number;
  icon: string;
  cls: string;
}) {
  return (
    <span className="flex items-center gap-1.5">
      <i className={`fa-solid ${icon} text-[0.7rem] ${cls}`} />
      <dt className="sr-only">{label}</dt>
      <dd className="tabular-nums text-text-heading">{value}</dd>
      <span className="text-text-muted">{label}</span>
    </span>
  );
}

/** The register strip — small stat tiles, one line each, no chrome. */
function Registers({ data }: { data: ComplianceOverview }) {
  const tiles: { label: string; value: string; hint: string; to: string }[] = [];

  if (data.operating) {
    tiles.push({
      label: data.operating.periodName,
      value: `${data.operating.closed}/${data.operating.total}`,
      hint:
        data.operating.overdue > 0
          ? `${data.operating.overdue} overdue`
          : "occurrences closed",
      to: "/compliance/operating",
    });
  }
  if (data.policies) {
    tiles.push({
      label: "Policies",
      value: `${data.policies.published}/${data.policies.total}`,
      hint: data.policies.lapsed > 0 ? `${data.policies.lapsed} past review` : "published",
      to: "/compliance/policies",
    });
  }
  if (data.risks) {
    tiles.push({
      label: "Risks",
      value: `${data.risks.accepted}/${data.risks.total}`,
      hint:
        data.risks.aboveAppetite > 0
          ? `${data.risks.aboveAppetite} above appetite`
          : "accepted by owners",
      to: "/compliance/risks",
    });
  }
  if (data.soa) {
    tiles.push({
      label: "SoA",
      value: `${data.soa.total - data.soa.unresolved}/${data.soa.total}`,
      hint: data.soa.releasedVersion ? `v${data.soa.releasedVersion} released` : "not released",
      to: "/compliance/soa",
    });
  }
  if (data.people) {
    tiles.push({
      label: "People",
      value: `${data.people.clean}/${data.people.total}`,
      hint: `${data.people.active} active`,
      to: "/compliance/people",
    });
  }
  if (data.vendors) {
    tiles.push({
      label: "Vendors",
      value: `${data.vendors.clean}/${data.vendors.total}`,
      hint:
        data.vendors.assuranceLapsing > 0
          ? `${data.vendors.assuranceLapsing} assurance lapsing`
          : `${data.vendors.critical} critical`,
      to: "/compliance/vendors",
    });
  }
  if (data.audit) {
    tiles.push({
      label: "Findings",
      value: String(data.audit.openFindings),
      hint:
        data.audit.openMajor > 0
          ? `${data.audit.openMajor} major open`
          : `${data.audit.awaitingVerification} awaiting verification`,
      to: "/compliance/audit",
    });
  }

  if (tiles.length === 0) return null;

  return (
    <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
      {tiles.map((t) => (
        <Link
          key={t.label}
          to={t.to}
          className="rounded-token-lg border border-border bg-bg-card p-3 transition-colors hover:bg-bg-card-hover"
        >
          <div className="truncate text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted">
            {t.label}
          </div>
          <div className="mt-1 font-display text-xl font-semibold text-text-heading">{t.value}</div>
          <div className="truncate text-[11px] text-text-muted">{t.hint}</div>
        </Link>
      ))}
    </div>
  );
}
